// src/firebase/registration.js
// Student Registration Service

import { createUserWithEmailAndPassword, sendEmailVerification, signOut } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './config';
import { getCohortByCode, incrementCohortStudentCount } from './cohorts';
import { DEFAULT_ORG_ID } from './orgs';
import { LEGAL_VERSIONS } from '../legal/legalVersions';

/**
 * Register a new student
 */
export const registerStudent = async (registrationData) => {
  try {
    const { name, email, password, phone, cohortCode, orgId, agreedToTerms } = registrationData;

    if (agreedToTerms !== true) {
      throw new Error('Please confirm you agree to the Terms and Conditions and have read the Privacy Policy.');
    }

    // Validate cohort code. orgId (from a ?org=<orgId> registration link)
    // scopes the lookup to one tutor's cohorts - codes are only unique
    // within an org, not globally (see cohorts.js) - and is optional for
    // manual entry / a link shared before this scoping existed.
    const cohort = await getCohortByCode(cohortCode, orgId);

    if (!cohort) {
      throw new Error('Invalid cohort code. Please check with your teacher.');
    }

    if (cohort.ambiguous) {
      throw new Error('This cohort code matches more than one tutor. Please use the registration link your tutor sent you, or double-check the code with them.');
    }

    if (!cohort.active) {
      throw new Error('This cohort is no longer accepting registrations.');
    }
    
    // Create Firebase Auth account
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // This tutor's registration settings - email verification and tutor
    // approval - read via a Cloud Function: a brand-new registrant can't read
    // the tutor's settings themselves (no profile yet for the rules to
    // check). Reading requireEmailVerification directly here was silently
    // refused and treated as "off" from 2026-08-25 until this fix.
    // firestore.rules enforces that both flags match the tutor's settings.
    let requireVerification = false;
    let requireApproval = false;
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const { getApp } = await import('firebase/app');
      const res = await httpsCallable(getFunctions(getApp()), 'getOrgRegistrationSettings')({ orgId: cohort.orgId || DEFAULT_ORG_ID });
      requireVerification = res.data.requireEmailVerification === true;
      requireApproval = res.data.requireStudentApproval === true;
    } catch (settingsError) {
      console.error('Registration settings lookup failed:', settingsError);
    }

    // Build user profile
    const userDocData = {
      name,
      email,
      phone,
      role: 'student',
      orgId: cohort.orgId || DEFAULT_ORG_ID,
      cohortId: cohort.id,
      cohortCode: cohort.code,
      cohortName: cohort.name,
      // Ticked "I agree" on the registration form (StudentRegistration.jsx)
      termsAcceptedVersion: LEGAL_VERSIONS.terms,
      privacyPolicyVersion: LEGAL_VERSIONS.privacy,
      termsAcceptedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      active: true
    };

    if (requireVerification) {
      userDocData.emailVerificationRequired = true;
    }
    if (requireApproval) {
      userDocData.approvalPending = true;
    }

    // Create user profile in Firestore
    await setDoc(doc(db, 'users', user.uid), userDocData);

    // Increment cohort student count
    await incrementCohortStudentCount(cohort.id);

    if (requireVerification) {
      try {
        await sendEmailVerification(user);
      } catch (emailError) {
        // Log but don't fail — the user doc is created and we still need to sign them out
        console.error('Failed to send verification email:', emailError);
      }
      // Send registration notification while still authenticated (before signOut)
      try {
        const { sendDirectNotification, NOTIFICATION_TYPES } = await import('./headMarkerNotifications');
        // Awaited: the notification needs the student signed in, and
        // signOut comes straight after
        await sendDirectNotification(NOTIFICATION_TYPES.NEW_REGISTRATION, {
          studentName: name,
          studentEmail: email,
          studentPhone: phone || '',
          cohortCode: cohort.code
        }, cohort.orgId || DEFAULT_ORG_ID);
      } catch (e) { console.error('Registration notification error:', e); }
      // Sign the user out so they can't bypass verification by going straight to the app
      await signOut(auth);
    }

    console.log('Student registered:', user.uid);

    return {
      uid: user.uid,
      name,
      email,
      role: 'student',
      requiresVerification: requireVerification
    };
    
  } catch (error) {
    console.error('Registration error:', error);
    
    // Provide user-friendly error messages
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('This email is already registered. Please log in instead.');
    } else if (error.code === 'auth/weak-password') {
      throw new Error('Password must be at least 6 characters long.');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('Please enter a valid email address.');
    } else {
      throw error;
    }
  }
};